import * as THREE from 'three';
import { Vector3 } from 'three';

import {
  CadFile,
  DynamicAssetCommonPropertiesModel,
  DynamicAssetGeometryPropertiesModel,
  DynamicAssetModel,
} from '../../../../../typings/api/skymap/rest/v0/.common';
import { PoiModel } from '../../../../../typings/api/skymap/rest/v1/.common';
import { isDefined } from '../../../utils/variables';
import { FileVersionTransformation } from '../../elements/components/dynamic-asset-file-version-transform';
import { Design } from '../../elements/design';
import { Geometry } from '../../elements/geometry';
import { Glb } from '../../elements/glb';
import { Poi, PoiProperties } from '../../elements/poi/poi';
import { DxfFactory } from '../dxf/dxf-factory';

/**
 * @param data
 * @description Extract points from the data
 * @returns {Promise<Array<THREE.Vector>>}
 */
function extractPoints(data: DynamicAssetModel) {
  return data.coordinates
    ? data.coordinates.map((coords: any) => {
        return new Vector3(Number(coords.lng), Number(coords.lat), Number(coords.height));
      })
    : undefined;
}

/**
 * @description Asset factory is used to create the assets loaded from the database
 */
export class AssetFactory {
  static get instance() {
    return AssetFactory._instance;
  }

  private static _instance: AssetFactory = new AssetFactory();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  /**
   * @description Load assets based on the data from the database
   * @param {*} data
   * @param sharedViewId
   * @returns {Promise<ElementObj>}
   */
  buildAsset(
    data: DynamicAssetModel | PoiModel,
    sharedViewId: string | undefined,
    cadFile?: CadFile,
  ) {
    const isDynamicAsset = isDefined((data as DynamicAssetModel).properties);
    return isDynamicAsset
      ? this.buildDynamicAsset(data as DynamicAssetModel, sharedViewId, cadFile)
      : this.buildPoiAsset(data as PoiModel);
  }

  private buildDynamicAsset(
    data: DynamicAssetModel,
    sharedViewId: string | undefined,
    cadFile?: CadFile,
  ) {
    if (!data || !data.type) {
      throw 'data is invalid';
    }

    try {
      // todo: Remove Line and Area and migrate entries in database
      // todo: to type Geometry instead.
      switch (data.type) {
        case 'Geometry': {
          return new Geometry(extractPoints(data), {
            ...(data.properties as DynamicAssetGeometryPropertiesModel),
            timelineContainerId: data.timelineContainerId,
            // To prevent slow loading always set it to false, unless it's a shared view.
            visible: sharedViewId ? data.properties.visible : false,
            assetId: data.id,
            skyMapVolumeSourceIds: data.skyMapVolumeSourceIds,
            prismVolumes: data.targetPrismVolumes,
            assetTagIds: data.assetTags?.map((tag) => tag.id) ?? [],
          });
        }
        case 'Design': {
          const properties = data.properties as DynamicAssetCommonPropertiesModel;
          return new Design({
            assetId: data.id,
            sharedViewId: sharedViewId,
            timelineContainerId: data.timelineContainerId,
            ...properties,
            MAS: properties.MAS,
            // To prevent slow loading always set it to false, unless it's a shared view.
            visible: sharedViewId ? data.properties.visible : false,
            skyMapVolumeSourceIds: data.skyMapVolumeSourceIds,
            prismVolumes: data.targetPrismVolumes,
            assetTagIds: data.assetTags?.map((tag) => tag.id) ?? [],
          });
        }
        case 'Dxf':
          if (!cadFile) {
            throw Error('Unable to create Dxf asset, no file info was provided.');
          }

          return DxfFactory.instance().loadDXFAsset(data, cadFile, sharedViewId);
        case 'Glb':
          if (!cadFile) {
            console.log(data, cadFile);
            throw Error('Unable to create Glb asset, no file info was provided.');
          }

          return new Glb(data.id, sharedViewId, {
            fileInfo: {
              ...cadFile.file,
              path: cadFile.path,
            },
            pdfs: data.pdfs,
            fileId: data.fileId,
            dynamicAssetFileVersionTransforms:
              data.dynamicAssetFileVersionTransforms as FileVersionTransformation[],
            timelineContainerId: data.timelineContainerId,
            ...(data.properties as DynamicAssetCommonPropertiesModel),
            skyMapVolumeSourceIds: data.skyMapVolumeSourceIds,
            prismVolumes: data.targetPrismVolumes,
            assetTagIds: data.assetTags?.map((tag) => tag.id) ?? [],
            // Always start hidden so that setStateVisibility(true) transitions false → true,
            // triggering loadModelData(). Without this, a Glb with properties.visible=true
            // would call setStateVisibility(true) in the constructor against the default
            // stateVisibility=true, making it a no-op and preventing the file from loading.
            // The asset-manager re-applies visibility (including shared-view overrides) after creation.
            visible: false,
          });
        default:
          break;
      }
    } catch (err) {
      console.log(err);
      return undefined;
    }
  }

  private buildPoiAsset(data: PoiModel) {
    if (!isDefined(data)) {
      throw new Error('POI data is undefined or null');
    }

    return new Poi(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      {
        ...data,
        visible: false,
      } satisfies PoiProperties,
      undefined,
    );
  }
}
